/**
 * Compliance Roster Page
 *
 * Editable single source of truth that maps each compliance role
 * (Security Officer, Privacy Officer, IT Asset Manager, etc.) to a
 * named individual + email. Used by Policy PDFs to auto-render a
 * "Personnel Assignments" section.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Save, UserCheck, Mail, Plus, Trash2, Search } from 'lucide-react';
import { Card } from '../components/ui-custom/ComplianceUI';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

export default function RosterPage() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingAll, setSavingAll] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [newRole, setNewRole] = useState({ title: '', description: '' });

  const fetchRoster = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/roster`, { credentials: 'include' });
      if (!res.ok) {
        toast.error('Failed to load roster');
        return;
      }
      const data = await res.json();
      setRoles(data.roles || []);
      // Seed local drafts from server values
      const next = {};
      (data.roles || []).forEach((r) => {
        next[r.role_key] = {
          person_name: r.person_name || '',
          person_email: r.person_email || '',
          backup_name: r.backup_name || '',
          backup_email: r.backup_email || '',
        };
      });
      setDrafts(next);
    } catch (e) {
      console.error(e);
      toast.error('Roster load error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoster();
  }, [fetchRoster]);

  const isDirty = (role) => {
    const d = drafts[role.role_key] || {};
    return (
      d.person_name !== (role.person_name || '') ||
      d.person_email !== (role.person_email || '') ||
      d.backup_name !== (role.backup_name || '') ||
      d.backup_email !== (role.backup_email || '')
    );
  };

  const updateField = (roleKey, field, value) => {
    setDrafts((prev) => ({
      ...prev,
      [roleKey]: { ...(prev[roleKey] || {}), [field]: value },
    }));
  };

  const saveAll = async () => {
    const dirtyRoles = roles.filter((r) => isDirty(r));
    if (dirtyRoles.length === 0) {
      toast.info('No changes to save');
      return;
    }

    // Validate emails for every dirty draft up front.
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const r of dirtyRoles) {
      const d = drafts[r.role_key] || {};
      if (d.person_email && !emailRe.test(d.person_email)) {
        toast.error(`Invalid primary email on "${r.title}"`);
        return;
      }
      if (d.backup_email && !emailRe.test(d.backup_email)) {
        toast.error(`Invalid backup email on "${r.title}"`);
        return;
      }
    }

    setSavingAll(true);
    try {
      const results = await Promise.all(
        dirtyRoles.map((role) => {
          const d = drafts[role.role_key] || {};
          return fetch(`${API_URL}/api/roster/${role.role_key}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              person_name: d.person_name || null,
              person_email: d.person_email || null,
              backup_name: d.backup_name || null,
              backup_email: d.backup_email || null,
            }),
          }).then(async (res) => ({ role, ok: res.ok, err: res.ok ? null : await res.json().catch(() => ({})) }));
        })
      );

      const failed = results.filter((r) => !r.ok);
      const succeeded = results.length - failed.length;

      if (failed.length === 0) {
        toast.success(`Saved ${succeeded} role${succeeded === 1 ? '' : 's'}`);
      } else if (succeeded > 0) {
        toast.warning(`Saved ${succeeded}, ${failed.length} failed: ${failed.map((f) => f.role.title).join(', ')}`);
      } else {
        toast.error(`Save failed for all ${failed.length} role${failed.length === 1 ? '' : 's'}`);
      }

      await fetchRoster();
    } catch (e) {
      console.error(e);
      toast.error('Save error');
    } finally {
      setSavingAll(false);
    }
  };

  const handleAddRole = async () => {
    if (!newRole.title.trim()) {
      toast.error('Role title is required');
      return;
    }
    const role_key = newRole.title.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    try {
      const res = await fetch(`${API_URL}/api/roster`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          role_key,
          title: newRole.title.trim(),
          description: newRole.description.trim() || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not create role');
        return;
      }
      toast.success(`Added "${newRole.title}"`);
      setNewRole({ title: '', description: '' });
      setShowAdd(false);
      await fetchRoster();
    } catch (e) {
      console.error(e);
      toast.error('Create error');
    }
  };

  const handleDeleteRole = async (role) => {
    if (!window.confirm(`Remove role "${role.title}"? This will also drop any assigned person.`)) return;
    try {
      const res = await fetch(`${API_URL}/api/roster/${role.role_key}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (!res.ok) {
        toast.error('Delete failed');
        return;
      }
      toast.success(`Removed "${role.title}"`);
      await fetchRoster();
    } catch (e) {
      console.error(e);
      toast.error('Delete error');
    }
  };

  const filtered = roles.filter((r) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      r.title?.toLowerCase().includes(q) ||
      r.person_name?.toLowerCase().includes(q) ||
      r.person_email?.toLowerCase().includes(q)
    );
  });

  const assignedCount = roles.filter((r) => r.person_name && r.person_email).length;
  const dirtyCount = roles.filter((r) => isDirty(r)).length;

  // Warn before navigating away with unsaved changes
  useEffect(() => {
    const handler = (e) => {
      if (dirtyCount > 0) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCount]);

  return (
    <div className="roster-page" data-testid="roster-page">
      <div className="page-header">
        <div>
          <h1>Compliance Roster</h1>
          <p>
            Single source of truth for who owns each compliance role.
            Assignments shown here appear automatically in policy PDFs as a
            "Personnel Assignments" section — no policy revisions needed when
            staff change.
          </p>
        </div>
        <div className="roster-stats">
          <div className="roster-stat">
            <span className="stat-value">{assignedCount}</span>
            <span className="stat-label">Assigned</span>
          </div>
          <div className="roster-stat">
            <span className="stat-value">{roles.length - assignedCount}</span>
            <span className="stat-label">Unassigned</span>
          </div>
        </div>
      </div>

      <div className="roster-toolbar">
        <div className="roster-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search by role, name, or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="roster-search"
          />
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {dirtyCount > 0 && (
            <span
              className="roster-dirty-pill"
              data-testid="roster-dirty-count"
              style={{
                background: '#fef3c7',
                color: '#92400e',
                border: '1px solid #fcd34d',
                padding: '4px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {dirtyCount} unsaved {dirtyCount === 1 ? 'change' : 'changes'}
            </span>
          )}
          <button
            className="btn-primary"
            disabled={dirtyCount === 0 || savingAll}
            onClick={saveAll}
            data-testid="roster-save-all"
            style={{
              opacity: dirtyCount === 0 ? 0.65 : 1,
              cursor: dirtyCount === 0 ? 'not-allowed' : 'pointer',
              padding: '10px 18px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            <Save size={16} />
            {savingAll
              ? 'Saving…'
              : dirtyCount > 0
                ? `Save ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}`
                : 'Save All'}
          </button>
          <button
            className="btn-secondary"
            onClick={() => setShowAdd(!showAdd)}
            data-testid="roster-add-toggle"
          >
            <Plus size={16} /> {showAdd ? 'Cancel' : 'Add Custom Role'}
          </button>
        </div>
      </div>

      {showAdd && (
        <Card className="roster-add-card" data-testid="roster-add-card">
          <h3>Add a new role</h3>
          <div className="roster-form-row">
            <label>
              Role title
              <input
                type="text"
                placeholder="e.g. Cloud Security Architect"
                value={newRole.title}
                onChange={(e) => setNewRole({ ...newRole, title: e.target.value })}
                data-testid="roster-new-title"
              />
            </label>
            <label>
              Short description (optional)
              <input
                type="text"
                placeholder="What this role owns"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                data-testid="roster-new-description"
              />
            </label>
          </div>
          <button className="btn-primary" onClick={handleAddRole} data-testid="roster-create-role">
            Create role
          </button>
        </Card>
      )}

      {loading ? (
        <div className="roster-loading">Loading roster…</div>
      ) : (
        <div className="roster-grid">
          {filtered.length === 0 ? (
            <div className="empty-state">No roles match your search.</div>
          ) : (
            filtered.map((role) => {
              const draft = drafts[role.role_key] || {};
              const dirty = isDirty(role);
              const assigned = role.person_name && role.person_email;
              return (
                <Card
                  key={role.role_key}
                  className={`roster-card ${assigned ? 'assigned' : 'unassigned'}`}
                  data-testid={`roster-card-${role.role_key}`}
                >
                  <div className="roster-card-header">
                    <div className="roster-role-meta">
                      <UserCheck size={18} className="roster-icon" />
                      <div>
                        <h3>{role.title}</h3>
                        {role.description && <p className="roster-desc">{role.description}</p>}
                      </div>
                    </div>
                    <button
                      className="roster-delete"
                      onClick={() => handleDeleteRole(role)}
                      title="Remove this role"
                      data-testid={`roster-delete-${role.role_key}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  <div className="roster-fields">
                    <label>
                      Primary owner
                      <input
                        type="text"
                        placeholder="Full name"
                        value={draft.person_name || ''}
                        onChange={(e) => updateField(role.role_key, 'person_name', e.target.value)}
                        data-testid={`roster-name-${role.role_key}`}
                      />
                    </label>
                    <label>
                      <Mail size={12} /> Primary email
                      <input
                        type="email"
                        placeholder="name@eyvo.com"
                        value={draft.person_email || ''}
                        onChange={(e) => updateField(role.role_key, 'person_email', e.target.value)}
                        data-testid={`roster-email-${role.role_key}`}
                      />
                    </label>
                    <label>
                      Backup (optional)
                      <input
                        type="text"
                        placeholder="Full name"
                        value={draft.backup_name || ''}
                        onChange={(e) => updateField(role.role_key, 'backup_name', e.target.value)}
                        data-testid={`roster-backup-name-${role.role_key}`}
                      />
                    </label>
                    <label>
                      <Mail size={12} /> Backup email
                      <input
                        type="email"
                        placeholder="backup@eyvo.com"
                        value={draft.backup_email || ''}
                        onChange={(e) => updateField(role.role_key, 'backup_email', e.target.value)}
                        data-testid={`roster-backup-email-${role.role_key}`}
                      />
                    </label>
                  </div>

                  <div className="roster-card-footer">
                    {role.updated_at && role.updated_by && role.updated_by !== 'system_seed' && (
                      <span className="roster-updated">
                        Last updated by {role.updated_by}
                      </span>
                    )}
                    {dirty && (
                      <span
                        data-testid={`roster-dirty-${role.role_key}`}
                        style={{
                          marginLeft: 'auto',
                          color: '#92400e',
                          fontSize: 12,
                          fontWeight: 600,
                        }}
                      >
                        ● Unsaved
                      </span>
                    )}
                  </div>
                </Card>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
